<template>
    <div class="relative inline-block text-left">
        <AppButton element="button" @click="open = !open" type="button">
            <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                    fill-rule="evenodd"
                    d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                    clip-rule="evenodd"
                ></path>
            </svg>
            {{ $t('messages.create') }}
            <SlideOver size="xxl" :show="open" @close="open = false">
                <div class="mt-1">
                    <FormLabel :value="$t('messages.title')" for="title" />
                    <FormInput
                        required
                        id="title"
                        v-model="form.title"
                        name="title"
                        type="text"
                        max="191"
                        class="mt-1 w-full"
                    />
                    <FormError :value="form.errors.title" class="mt-2" />
                </div>

                <div class="sm:col-span-4">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.grade_level')" for="grade_level_id" />
                        <div>
                            <label class="ml-1 mr-3" v-for="gradeLevel in gradeLevels" :key="gradeLevel.value">
                                <input
                                    :id="`grade_level_ids_${gradeLevel.value}`"
                                    name="grade_level_ids[]"
                                    :value="gradeLevel.value"
                                    type="checkbox"
                                    v-model="form.grade_level_ids"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                {{ gradeLevel.text }}
                            </label>

                            <FormError :value="form.errors.grade_level_ids" class="mt-2" />
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-4">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.domains', 1)" for="category_id" />
                        <FormSelect
                            required
                            id="category"
                            v-model.number="form.category_id"
                            :options="categories"
                            class="mt-1 w-full"
                            property-name="name"
                            property-value="id"
                        />

                        <FormError :value="form.errors.category_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-4">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.competences', 2)" for="competences" />
                        <FormError :value="form.errors.competences_ids" class="mt-2" />
                        <div v-if="selectedCategory !== null">
                            <div
                                class="sm:col-span-1"
                                v-for="dimension in selectedCategory.dimensions"
                                :key="dimension.id"
                            >
                                <label class="block text-sm font-medium leading-5 text-gray-700">
                                    {{ dimension.abbreviation }} -
                                    {{ dimension.title }}
                                </label>
                                <ul class="m-3">
                                    <li
                                        v-for="(competence, competenceIndex) in dimension.competences"
                                        :key="`competence_${competence.id}`"
                                        :class="{
                                            'bg-gray-100': competenceIndex % 2 === 0,
                                        }"
                                    >
                                        {{ competence.abbreviation }} -

                                        <input
                                            :id="`competences_ids_${competence.id}`"
                                            name="competences_ids[]"
                                            :value="competence.id"
                                            type="checkbox"
                                            v-model="form.competences_ids"
                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                        <div class="text-sm inline-block">
                                            {{ competence.description }}
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <form action="" method="post" @submit.prevent="submit" class="mt-5">
                    <div class="flex justify-end">
                        <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
                    </div>
                </form>
            </SlideOver>
        </AppButton>
    </div>
</template>

<script>
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import SlideOver from '@/components/SlideOver.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import AppButton from '@/components/AppButton.vue';

export default {
    emits: ['close', 'include-new-content'],
    name: 'SyllabusContentCreateForm',

    components: {
        AppButton,
        SubmitFormButton,
        SlideOver,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
    },

    props: {
        category: {
            required: false,
            default: null,
        },
        categories: {
            required: true,
            type: Array,
        },
        gradeLevels: {
            required: true,
            type: Array,
        },
    },

    computed: {
        selectedCategory: function () {
            if (!this.form.category_id) {
                return null;
            }

            return this.categories.find((c) => c.id === this.form.category_id);
        },
    },

    watch: {
        'form.category_id': {
            handler() {
                this.form.competences_ids = [];
            },
            deep: true,
        },
    },

    data() {
        return {
            open: false,
            form: this.$inertia.form({
                category_id: this.category,
                title: '',
                grade_level_ids: [],
                competences_ids: [],
            }),
        };
    },

    methods: {
        dismiss() {
            this.$emit('close');
        },
        submit() {
            this.form.post(this.route('syllabus-content.store'), {
                onSuccess: () => {
                    this.open = false;
                    this.form.title = '';
                    this.form.competences_ids = [];
                    this.$emit('include-new-content');
                },
                preserveScroll: true,
            });
        },
    },
};
</script>
